/**
 * BreedTab — pair-and-breed UI.
 *
 * Multi-instance refactor (2026-06-08): each row in the user's box is
 * its own distinct instance with its own passives/talents/lucky flag,
 * so the parent picker is keyed on `pal_id` (not species name). You
 * can pick two instances of the same species — exactly what players
 * want when chaining breeds for better passives. Parents are no
 * longer consumed (cost is 2× the legacy single-parent price).
 */
import { useEffect, useState, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Currency, Loader, RarityBadge } from "@/components/Atoms";
import { PalPortrait, ElementChip } from "@/components/PalAssets";

export function BreedTab() {
  const [pals, setPals] = useState([]);
  // `id1`/`id2` are concrete pal_id ints — strings in state because
  // <select> values are always strings.
  const [id1, setId1] = useState("");
  const [id2, setId2] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const toast = useToast();
  const { refreshProfile } = useAuth();

  const reload = () =>
    api.get("/palgame/box").then(({ data }) => setPals(data.pals || []));
  useEffect(() => { reload(); }, []);

  const pal1Row = useMemo(() => pals.find((p) => String(p.pal_id) === id1), [pals, id1]);
  const pal2Row = useMemo(() => pals.find((p) => String(p.pal_id) === id2), [pals, id2]);

  const passivesOf = (row) => {
    if (!row) return [];
    try { return JSON.parse(row.passives || "[]") || []; }
    catch { return []; }
  };
  const parent1Passives = passivesOf(pal1Row);
  const parent2Passives = passivesOf(pal2Row);
  const inheritancePool = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const n of [...parent1Passives, ...parent2Passives]) {
      if (n && !seen.has(n)) { seen.add(n); out.push(n); }
    }
    return out;
  }, [parent1Passives, parent2Passives]);

  // Live preview whenever both parents picked. The /breed GET
  // calculator works on species names — pull them from the chosen
  // rows.
  useEffect(() => {
    if (!pal1Row || !pal2Row) { setPreview(null); setPreviewError(""); return; }
    let cancelled = false;
    setPreviewBusy(true);
    setPreviewError("");
    api.get("/palgame/breed", {
      params: { pal1: pal1Row.pal_name, pal2: pal2Row.pal_name },
    })
      .then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch((e) => { if (!cancelled) { setPreview(null); setPreviewError(apiError(e)); } })
      .finally(() => { if (!cancelled) setPreviewBusy(false); });
    return () => { cancelled = true; };
  }, [pal1Row, pal2Row]);

  // Same-instance is the only forbidden case now — same-species (two
  // separate Foxparks) is a valid breed.
  const sameInstance = id1 && id2 && id1 === id2;

  const breed = async () => {
    if (!id1 || !id2) { toast.error("Pick two Pals."); return; }
    if (sameInstance) {
      toast.error("Pick two different Pals — you can't breed a Pal with itself.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/palgame/breed", {
        pal1_id: parseInt(id1, 10),
        pal2_id: parseInt(id2, 10),
      });
      setLast(data);
      const luckyTag = data.is_lucky ? " ✨ LUCKY" : "";
      toast.success(`Bred into ${data.result}${luckyTag}`);
      // Keep the parent selections so the user can quickly chain
      // another breed; just reset the result-preview placeholder so
      // the next live preview can render once they tweak picks.
      setPreview(null);
      reload();
      refreshProfile();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="glass-card p-7">
          <h3 className="label mb-1">Breed Pals</h3>
          <p className="text-xs text-[var(--muted)] mb-4">
            Pick the exact two Pals to breed — duplicates of the same
            species are fair game. Parents are <strong>preserved</strong>,
            so you can chain breeds for better passives. Offspring
            inherits passives from the union of parent pools (50% per
            unique slot, +15% mutation chance for one fresh trait).
            Talents are re-rolled.
          </p>

          <div className="space-y-3">
            <BreedPalPicker
              value={id1}
              onChange={setId1}
              pals={pals}
              testid="breed-p1"
              placeholder="Parent 1…"
              excludeId={id2}
            />
            <BreedPalPicker
              value={id2}
              onChange={setId2}
              pals={pals}
              testid="breed-p2"
              placeholder="Parent 2…"
              excludeId={id1}
            />

            {/* Parent passive panels — show what's available for inheritance */}
            {(pal1Row || pal2Row) && (
              <div className="grid grid-cols-2 gap-2 pt-2">
                <ParentPassivesPanel
                  label={pal1Row ? `${pal1Row.pal_name} · Lv ${pal1Row.level}` : "Parent 1"}
                  passives={parent1Passives}
                  testid="breed-parent1-passives"
                />
                <ParentPassivesPanel
                  label={pal2Row ? `${pal2Row.pal_name} · Lv ${pal2Row.level}` : "Parent 2"}
                  passives={parent2Passives}
                  testid="breed-parent2-passives"
                />
              </div>
            )}

            <button
              onClick={breed}
              disabled={busy || !id1 || !id2 || sameInstance}
              className="btn btn-warning w-full"
              data-testid="breed-submit"
            >
              {busy ? "Breeding…" : sameInstance ? "Pick two different Pals" : "Breed"}
            </button>
            {previewError && !sameInstance && (
              <div className="text-xs text-[var(--warning)]" data-testid="breed-preview-error">
                {previewError}
              </div>
            )}
          </div>
        </div>

        {/* Preview / Result card */}
        <div className="glass-card p-7 flex items-center justify-center">
          {last ? (
            <div className={`text-center p-4 border-2 rounded-lg w-full rarity-${(last.rarity || "common").toLowerCase()}`} data-testid="breed-result">
              {last.is_lucky && (
                <div className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-900/60 border border-yellow-400/40 text-[10px] uppercase tracking-[0.25em] text-yellow-200"
                     data-testid="breed-lucky-banner">
                  ✨ Lucky!
                </div>
              )}
              <div className="text-xs uppercase tracking-[0.2em] mb-1 opacity-70">Bred</div>
              <PalPortrait name={last.result} size="w-32 h-32 mx-auto mb-3" />
              <div className="text-3xl mb-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{last.result}</div>
              <RarityBadge rarity={last.rarity} />
              <div className="mt-3 flex flex-wrap justify-center gap-1">
                {(last.elements || []).map((e) => <ElementChip key={e} element={e} size={14} />)}
              </div>
              {Array.isArray(last.passives_inherited) && last.passives_inherited.length > 0 && (
                <div className="mt-3" data-testid="breed-inherited-passives">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)] mb-1">
                    Inherited Passives
                  </div>
                  <div className="flex flex-wrap justify-center gap-1 max-w-full">
                    {last.passives_inherited.map((p) => (
                      <span key={p}
                            className="text-[10px] px-2 py-0.5 rounded border border-white/15 bg-white/5 text-white/80 uppercase tracking-wider">
                        {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {last.talents && (
                <div className="mt-3 text-[10px] text-[var(--muted)] flex justify-center gap-3 font-mono">
                  <span className="text-emerald-300/80">HP {last.talents.hp}</span>
                  <span className="text-rose-300/80">ATK {last.talents.atk}</span>
                  <span className="text-sky-300/80">DEF {last.talents.def}</span>
                </div>
              )}
              <div className="text-xs text-[var(--muted)] mt-3">
                Lv {last.result_level} · cost <Currency value={last.cost} />
              </div>
              <div className="text-[10px] text-emerald-300/80 mt-1" data-testid="breed-parents-kept">
                Parents preserved — breed again for an even better roll.
              </div>
            </div>
          ) : preview ? (
            <div className={`text-center p-4 border-2 border-dashed rounded-lg w-full rarity-${(preview.result_rarity || "common").toLowerCase()}`} data-testid="breed-preview">
              <div className="text-xs uppercase tracking-[0.2em] mb-1 opacity-70">
                Predicted Offspring {preview.special && <span className="ml-1 text-yellow-300">★ Special Combo</span>}
              </div>
              <PalPortrait name={preview.result} size="w-28 h-28 mx-auto mb-3" />
              <div className="text-2xl mb-2" style={{ fontFamily: "'Cinzel Decorative', serif" }}>{preview.result}</div>
              <RarityBadge rarity={preview.result_rarity} />
              <div className="mt-3 flex flex-wrap justify-center gap-1">
                {(preview.result_elements || []).map((e) => <ElementChip key={e} element={e} size={14} />)}
              </div>
              <div className="text-[10px] text-[var(--muted)] mt-3 font-mono">{preview.formula}</div>
              {inheritancePool.length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)] mb-1">
                    Inheritance Pool ({inheritancePool.length})
                  </div>
                  <div className="flex flex-wrap justify-center gap-1 max-w-full">
                    {inheritancePool.map((p) => (
                      <span key={p}
                            className="text-[10px] px-2 py-0.5 rounded border border-white/10 bg-white/5 text-white/70 uppercase tracking-wider">
                        {p}
                      </span>
                    ))}
                  </div>
                  <div className="text-[10px] text-[var(--muted)] mt-1">
                    Offspring rolls 50% per unique slot, up to 4.
                  </div>
                </div>
              )}
              <div className="text-[10px] text-[var(--muted)] mt-3 italic">
                Click &ldquo;Breed&rdquo; to lock the roll. Both parents stay in your box.
              </div>
            </div>
          ) : previewBusy ? (
            <Loader />
          ) : (
            <p className="text-[var(--muted)] text-sm">Pick two Pals to preview the offspring.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Picker that lists each owned instance as its own option, keyed on
 *  pal_id so two Foxparks can be picked independently. Shows level,
 *  Lucky flag, and passive count so the player can spot the strongest
 *  candidate at a glance. */
function BreedPalPicker({ value, onChange, pals, testid, placeholder, excludeId }) {
  return (
    <select
      className="input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
    >
      <option value="">{placeholder || "Select a Pal…"}</option>
      {pals.map((p) => {
        if (excludeId && String(p.pal_id) === excludeId) return null;
        let passiveCount = p.passive_count;
        if (passiveCount == null) {
          try { passiveCount = (JSON.parse(p.passives || "[]") || []).length; }
          catch { passiveCount = 0; }
        }
        const tags = [];
        if (p.is_lucky) tags.push("✨");
        if (passiveCount > 0) tags.push(`${passiveCount}P`);
        return (
          <option key={p.pal_id} value={String(p.pal_id)}>
            #{p.pal_id} {p.pal_name} (Lv {p.level}){tags.length ? ` · ${tags.join(" ")}` : ""}
          </option>
        );
      })}
    </select>
  );
}

function ParentPassivesPanel({ label, passives, testid }) {
  return (
    <div className="rounded border border-white/10 bg-white/5 p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mb-1 truncate">
        {label}
      </div>
      {passives.length === 0 ? (
        <div className="text-[10px] text-[var(--muted)] italic">No passives</div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {passives.map((p) => (
            <span key={p}
                  className="text-[9px] px-1.5 py-0.5 rounded border border-white/15 bg-white/5 text-white/80 uppercase tracking-wider">
              {p}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
